import { Box, LinearProgress, makeStyles, Typography } from '@material-ui/core';
import { DocumentForms, useDocumentStore } from '@roc/feature-documents';
import { useLoanStore } from '@roc/feature-loans';
import {
  flatten,
  LoanProcess,
  LoanStatus,
} from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { CustomCard } from '../customCard';
import { TaskTodoCardCompponent } from './todoTaskCard';
import { getTodosLengthWithoutParents, isPendingTodoStatus } from '../../utils/todoUtils';
import { Button } from '@roc/ui';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { useHistory, useRouteMatch } from 'react-router';
import { colors } from '../../utils/todoConstants';
import { useBaseStore } from '@roc/feature-app-core';
import { Alert } from '@material-ui/lab';
import { useLoanFlags } from '../../hooks/useLoanFlags';
import { LoanDrawsPendingTodos } from 'libs/feature-draws/src/draws/loanDrawsDashboard/components/loanDrawsPendingTodos';
import { useDrawsStore } from '@roc/feature-draws';

const useStyles = makeStyles((theme) => ({
  button: {
    color: theme.palette.primary.main,
    border: `solid 1px ${theme.palette.primary.main}`,
    borderRadius: '4px',
    padding: '4px 10px',
    fontSize: '15px',
    fontWeight: 700,
    marginRight: '10px',
  },
  borderLinearProgress: {
    height: '18px',
    borderRadius: 20,
    [`&.MuiLinearProgress-root`]: {
      backgroundColor: colors.brightGray,
    },
    [`& .MuiLinearProgress-bar`]: {
      borderRadius: 20,
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

export const LoanSummaryTodosTasksCard = observer(() => {
  const classes = useStyles();
  const { loanStore } = useLoanStore();
  const { documentStore } = useDocumentStore();
  const { drawStore } = useDrawsStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { push } = useHistory();
  const { loanDetails } = loanStore;
  const { loanTodosStore } = documentStore;
  const currentForm = documentStore.documentFormStore.currentForm;
  const loanId = loanDetails.loanId;
  const { showUnderwritingAndClosingTab, showPostClosing } = useLoanFlags();
  const showAllToDos = !documentStore?.globalStore?.userFeatures?.lenderUser &&
  !documentStore?.globalStore?.userFeatures?.backOfficeUser &&
  !documentStore?.globalStore?.userFeatures?.isClosingAttorney;
  const { showDraws } = loanDetails;
  const isAttorneyPortal = useRouteMatch('/attorney-portal');
  const showDrawsUI = showDraws && !isAttorneyPortal


  const properties = loanDetails?.properties || [];

  const showUnderwritingTodos = [
    LoanStatus.PENDING_SUBMISSION,
    LoanStatus.PENDING,
    LoanStatus.IN_UNDERWRITING,
  ].includes(loanDetails?.status);

  const showClosingTodos = [
    LoanStatus.PENDING_DECISION,
    LoanStatus.AGREED,
  ].includes(loanDetails?.status);

  useEffect(() => {
    loanTodosStore.setShowOnlyPending(!showAllToDos);
    loanTodosStore.setPrioritiesFilter([]);
  }, []);

  const fetchTodos = () => {
    if (showDrawsUI) {
      documentStore.loanTodosStore.fetchLoanDrawTodosPending(loanId);
    } else if (showUnderwritingAndClosingTab) {
      loanTodosStore.fetchTodosForMultipleLoanProcesses(loanId, LoanProcess.TODOS, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING]);
    } else if (showUnderwritingTodos) {
      documentStore.loanTodosStore.fetchTodos(loanId, LoanProcess.UNDERWRITING);
    } else if (showClosingTodos) {
      documentStore.loanTodosStore.fetchTodos(loanId, LoanProcess.CLOSING);
    }
    if (showPostClosing) {
      loanTodosStore.fetchPostClosingConditionTasks(loanId);
    }
  }

  useEffect(() => {
    fetchTodos();
  }, [loanId]);

  useEffect(() => {
    if (showDrawsUI) {
      drawStore.fetchDraws(loanId);
    }
    return () => {
      drawStore.resetDraws();
    };
  }, [drawStore, loanId, showDrawsUI]);

  const { globalStore } = useBaseStore();

  let displayTodos = [];
  let closingDocumentsCount = 0;

  if (showDrawsUI) {
    displayTodos = loanTodosStore.todosByLoanProcess[LoanProcess.DRAWS] ?? [];
  } else if (showUnderwritingAndClosingTab) {
    displayTodos = loanTodosStore.todosByLoanProcess[LoanProcess.TODOS] ?? [];
  } else if (showUnderwritingTodos) {
    displayTodos =
      loanTodosStore.todosByLoanProcess[LoanProcess.UNDERWRITING] ?? [];
  } else if (showClosingTodos) {
    let documents = documentStore.closingDocuments;
    closingDocumentsCount = flatten(Object.values(documents)).length;
    displayTodos = flatten(Object.values(documents)).map(doc =>
      documentStore.loanTodosStore.mapDocumentToTodo(doc, LoanProcess.CLOSING)
    );
  }

  displayTodos = displayTodos.filter(t => isPendingTodoStatus(t.status) || t?.document?.isParentTask);

  const isPaidOffStatus = [LoanStatus.PAID_OFF, LoanStatus.PAID_OFF_BUYOUT].includes(loanDetails?.status);
  const allTodos = loanTodosStore.todosByLoanProcess[LoanProcess.TODOS] ?? [];
  const pcTodos = allTodos.filter(t =>
    t.document?.postClosingCondition &&
    (isPendingTodoStatus(t.status) || t?.document?.isParentTask)
  );


  if (isPaidOffStatus) {
    const pcParentIds = new Set(pcTodos.map(t => t.parentTodoId).filter(Boolean));
    const pcParents = allTodos.filter(t => pcParentIds.has(t.todoId));
    displayTodos = [...pcParents, ...pcTodos];
  }

  const isTodosEmpty = displayTodos.length === 0;

  const ccTodos = documentStore.loanTodosStore.closingConditionsTodos ?? [];
  const ccPendingTodos = ccTodos.filter(t => isPendingTodoStatus(t.status) || t?.document?.unreadCount > 0);

  const hideTodosCard = isPaidOffStatus
    ? pcTodos.length === 0
    : [
        LoanStatus.DECLINED,
        LoanStatus.RETRACTED,
        LoanStatus.ON_HOLD,
        LoanStatus.MOVED_TO_EXTERNAL_CAPITAL,
      ].includes(loanDetails?.status);

  return (
    <>
      {!hideTodosCard && (
        <CustomCard paperNoShadow paperStyle={{ padding: '0px 8px 24px 8px' }} title={'To-Dos'}
          cardHeaderProps={{
            action: (
              <Box pt={1}>
                <Button className={classes.button} testId={'seeAllTodos'} unsetTextTransfrom unsetLetterSpacing onClick={() => {
                  if (!isPaidOffStatus && showDrawsUI) {
                    push(loanRoutesConfig.loans(loanId).children.draws.url)
                  }
                  else if (showUnderwritingAndClosingTab || isPaidOffStatus) {
                    push(loanRoutesConfig.loans(loanId).children.todos.url)
                  } else if (showUnderwritingTodos) {
                    push(loanRoutesConfig.loans(loanId).children.underwriting.url)
                  } else {
                    push(loanRoutesConfig.loans(loanId).children.closing.url)
                  }
                }}>
                  VIEW ALL
                </Button>
              </Box>
            ),
          }}
          cardContentProps={{
            style: {
              overflow: 'auto'
            }
          }}>
          {!showDrawsUI && !isPaidOffStatus && (<Box style={{
            padding: '4px 0 10px 0px'
          }}>
            <Typography style={{ marginLeft: '4px' }}>{`${getTodosLengthWithoutParents(displayTodos)} To-Do`}</Typography>
            <LinearProgress
              variant="determinate"
              value={showDrawsUI ? (
                displayTodos.length / loanTodosStore.todosByLoanProcess[LoanProcess.DRAWS]?.length * 100) :
                showUnderwritingAndClosingTab ? (
                  displayTodos.length / loanTodosStore.todosByLoanProcess[LoanProcess.TODOS]?.length * 100
                ) : (showUnderwritingTodos ? (
                  displayTodos.length / loanTodosStore.todosByLoanProcess[LoanProcess.UNDERWRITING]?.length * 100
                ) : (displayTodos.length / closingDocumentsCount * 100))}
              className={classes.borderLinearProgress} />
          </Box>)}
          <Box
            style={{
              position: 'relative',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {(!isPaidOffStatus && showDrawsUI) ? (
              <LoanDrawsPendingTodos loanId={loanId} properties={properties} />
            ) : (
              <>
                {isTodosEmpty ? (
                  <Box pt={3}>
                    <Alert severity="success">
                      You don't have any pending to-dos.
                    </Alert>
                  </Box>
                ) : (
                  <TaskTodoCardCompponent
                    todos={displayTodos}
                    ccTodos={ccPendingTodos}
                    loanProcess={
                      isPaidOffStatus ? LoanProcess.TODOS :
                      showDrawsUI ? LoanProcess.DRAWS :
                        showUnderwritingAndClosingTab ?
                          LoanProcess.TODOS :
                          showUnderwritingTodos
                            ? LoanProcess.UNDERWRITING
                            : LoanProcess.CLOSING
                    }
                    showSectionActions={!isPaidOffStatus && globalStore.isInternalPortal}
                    showFileName={!isPaidOffStatus && globalStore.isInternalPortal}
                    showAssigneeColumn
                    showStatusColumn
                    showPriorityColumn
                    showActions={true}
                    isSummaryView={true}
                  />
                )}
              </>
            )}
          </Box>
        </CustomCard>
      )}
      <DocumentForms loanId={loanDetails.loanId} onClose={fetchTodos} />
    </>
  );
});
